import i18n from '../i18n.js';
import { SUPPORTED_TYPES, MAX_FILE_SIZE } from '../lib/image-loader.js';

export class UploadArea {
  constructor(container, onFilesChanged, onSelectFile) {
    this.container = container;
    this.onFilesChanged = onFilesChanged;
    this.onSelectFile = onSelectFile;
    this.files = [];
    this.selectedIndex = 0;
    this.blobUrls = [];
    this.render();
    this.bindEvents();
  }

  render() {
    if (this.files.length === 0) {
      this.renderEmpty();
    } else {
      this.renderFiles();
    }
  }

  renderEmpty() {
    this.container.innerHTML =
      '<div class="upload-area" id="upload-area">' +
      '<div class="upload-icon">🖼️</div>' +
      '<div class="upload-title">' + i18n.t('upload.title') + '</div>' +
      '<div class="upload-subtitle">' + i18n.t('upload.subtitle') + '</div>' +
      '<div class="upload-formats"><span>PNG</span><span>JPEG</span><span>WebP</span></div>' +
      '<div class="upload-hint" style="margin-top:var(--space-3);font-size:var(--text-xs);color:var(--color-text-muted);">' + i18n.t('upload.hint') + '</div>' +
      '<input type="file" accept="image/png,image/jpeg,image/webp" multiple style="display:none;" id="file-input">' +
      '<div class="upload-error" id="upload-error"></div></div>';
    this.element = this.container.querySelector('#upload-area');
    this.errorEl = this.container.querySelector('#upload-error');
    this.fileInput = this.container.querySelector('#file-input');
  }

  renderFiles() {
    this.revokeBlobUrls();
    var self = this;
    var html = '<div class="upload-area has-files" id="upload-area">';

    /* Header row: count + add-more button */
    html += '<div class="upload-files-header">';
    html += '<span class="upload-file-count">' + this.files.length + ' ' + i18n.t('upload.files') + '</span>';
    html += '<button class="upload-add-btn" id="upload-add-more">+ ' + i18n.t('upload.addMore') + '</button>';
    html += '</div>';

    /* Hidden file input */
    html += '<input type="file" accept="image/png,image/jpeg,image/webp" multiple style="display:none;" id="file-input">';

    /* Thumbnail grid */
    html += '<div class="upload-thumb-grid" id="upload-thumb-grid">';
    this.files.forEach(function(file, i) {
      var url = URL.createObjectURL(file);
      self.blobUrls.push(url);
      var selected = i === self.selectedIndex ? ' selected' : '';
      html += '<div class="upload-thumb-item' + selected + '" data-index="' + i + '">';
      html += '<div class="upload-thumb-img-wrap">';
      html += '<img src="' + url + '" alt="' + file.name.replace(/"/g, '&quot;') + '" loading="lazy">';
      html += '<button class="upload-thumb-remove" data-index="' + i + '" title="' + i18n.t('upload.remove') + '">×</button>';
      html += '</div>';
      html += '<div class="upload-thumb-name" title="' + file.name.replace(/"/g, '&quot;') + '">' + file.name + '</div>';
      html += '</div>';
    });
    html += '</div>';

    /* Error area */
    html += '<div class="upload-error" id="upload-error"></div>';
    html += '</div>';

    this.container.innerHTML = html;
    this.element = this.container.querySelector('#upload-area');
    this.errorEl = this.container.querySelector('#upload-error');
    this.fileInput = this.container.querySelector('#file-input');
  }

  bindEvents() {
    var self = this;

    /* Click empty area to open file picker */
    this.element.addEventListener('click', function(e) {
      if (self.files.length > 0) return;
      /* Ignore clicks on children that have their own handlers */
      self.fileInput.click();
    });

    /* File input changed */
    this.container.addEventListener('change', function(e) {
      if (e.target.id === 'file-input' && e.target.files.length) {
        self.addFiles(Array.from(e.target.files));
        e.target.value = '';
      }
    });

    /* Add more button */
    this.container.addEventListener('click', function(e) {
      if (e.target.closest('#upload-add-more')) {
        self.fileInput.click();
      }
    });

    /* Remove button */
    this.container.addEventListener('click', function(e) {
      var btn = e.target.closest('.upload-thumb-remove');
      if (btn) {
        e.stopPropagation();
        self.removeFile(parseInt(btn.dataset.index));
      }
    });

    /* Select thumbnail */
    this.container.addEventListener('click', function(e) {
      var item = e.target.closest('.upload-thumb-item');
      if (item) {
        self.selectFile(parseInt(item.dataset.index));
      }
    });

    /* Drag-and-drop on empty area */
    this.element.addEventListener('dragover', function(e) {
      e.preventDefault();
      if (self.files.length === 0) self.element.classList.add('dragover');
    });
    this.element.addEventListener('dragleave', function(e) {
      e.preventDefault();
      self.element.classList.remove('dragover');
    });
    this.element.addEventListener('drop', function(e) {
      e.preventDefault();
      self.element.classList.remove('dragover');
      var accepted = Array.from(e.dataTransfer.files).filter(function(f) {
        return SUPPORTED_TYPES.indexOf(f.type) !== -1;
      });
      if (accepted.length) self.addFiles(accepted);
    });
  }

  addFiles(newFiles) {
    var valid = [];
    this.hideError();

    newFiles.forEach(function(f) {
      if (SUPPORTED_TYPES.indexOf(f.type) === -1) {
        this.showError(f.name + ': ' + i18n.t('upload.error.type'));
        return;
      }
      if (f.size > MAX_FILE_SIZE) {
        this.showError(f.name + ': ' + i18n.t('upload.error.size'));
        return;
      }
      /* Avoid duplicates by name and size */
      var dup = this.files.some(function(existing) {
        return existing.name === f.name && existing.size === f.size;
      });
      if (!dup) valid.push(f);
    }, this);

    if (valid.length === 0) return;

    var startIdx = this.files.length;
    this.files = this.files.concat(valid);
    this.selectedIndex = startIdx;
    this.render();
    this.bindEvents();
    this.onFilesChanged(this.files);
    this.onSelectFile(this.selectedIndex, this.files[this.selectedIndex]);
  }

  removeFile(index) {
    this.files.splice(index, 1);
    if (this.files.length === 0) {
      this.selectedIndex = 0;
      this.render();
      this.bindEvents();
      this.onFilesChanged(this.files);
      return;
    }
    if (this.selectedIndex >= this.files.length) {
      this.selectedIndex = this.files.length - 1;
    }
    this.render();
    this.bindEvents();
    this.onFilesChanged(this.files);
    this.onSelectFile(this.selectedIndex, this.files[this.selectedIndex]);
  }

  selectFile(index) {
    this.selectedIndex = index;
    /* Update selected class without full re-render */
    var items = this.element.querySelectorAll('.upload-thumb-item');
    items.forEach(function(el, i) {
      el.classList.toggle('selected', i === index);
    });
    this.onSelectFile(index, this.files[index]);
  }

  reset() {
    this.files = [];
    this.selectedIndex = 0;
    this.revokeBlobUrls();
    this.render();
    this.bindEvents();
  }

  revokeBlobUrls() {
    this.blobUrls.forEach(function(url) { URL.revokeObjectURL(url); });
    this.blobUrls = [];
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.classList.add('visible');
  }

  hideError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.classList.remove('visible');
    }
  }

  updateI18n() {
    this.render();
    this.bindEvents();
  }
}
